// AES256 유틸리티 함수 추가
import CryptoJS from 'crypto-js'

// 환경 변수에서 SECRET_KEY 가져오기
const SECRET_KEY = process.env.VUE_APP_SECRET_KEY;

// IV (Initialization Vector) - 서버와 동일하게 설정 (모든 바이트가 0인 16바이트)
const IV = CryptoJS.enc.Hex.parse('00000000000000000000000000000000'); // 16바이트 모두 0

// SECRET_KEY가 설정되지 않은 경우 경고
if (!SECRET_KEY) {
    console.error('SECRET_KEY가 환경 변수에 설정되지 않았습니다.');
    throw new Error('암호화 키가 설정되지 않았습니다.');
}

// 암호화된 데이터인지 확인하는 함수
function isEncrypted(data) {
    try {
        if (!data || typeof data !== 'string') {
            return false;
        }

        // Base64 형식인지 확인
        const base64Regex = /^[A-Za-z0-9+/]+=*$/;
        if (!base64Regex.test(data)) {
            return false;
        }
        
        // CryptoJS로 복호화 시도 (CBC 모드)
        const key = CryptoJS.enc.Utf8.parse(SECRET_KEY);
        const decrypted = CryptoJS.AES.decrypt(data, key, {
            iv: IV,
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7
        });
        
        const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);
        
        // 복호화가 성공하고 결과가 있으면 암호화된 데이터
        return decryptedText.length > 0;
    } catch (error) {
        // 복호화 실패하면 평문
        return false;
    }
}

// AES256 복호화 함수 (서버 호환 CBC 모드)
function decryptAES256(encryptedData, secretKey = SECRET_KEY) {
    try {
        if (!secretKey) {
            console.warn('암호화 키가 제공되지 않았습니다. 원본 데이터 반환');
            return encryptedData || '';
        }

        if (!encryptedData) {
            return '';
        }

        // 평문인지 확인
        if (!isEncrypted(encryptedData)) {
            return encryptedData; // 평문이면 그대로 반환
        }

        // 서버와 동일한 AES/CBC/PKCS5Padding 방식으로 복호화
        const key = CryptoJS.enc.Utf8.parse(secretKey);
        
        const decrypted = CryptoJS.AES.decrypt(encryptedData, key, {
            iv: IV,
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7
        });
        
        const decryptedData = decrypted.toString(CryptoJS.enc.Utf8);
        
        if (!decryptedData) {
            // 이미 암호화된 데이터인지 확인
            if (isEncrypted(encryptedData)) {
                console.log('이미 암호화된 데이터입니다. 원본 반환:', encryptedData);
            }
            return encryptedData;
        }
        
        return decryptedData;
    } catch (error) {
        console.error('AES256 복호화 오류:', error);
        // 복호화 실패 시 원본 데이터가 평문일 가능성이 있으므로 원본 반환
        if (import.meta.env.DEV) {
            console.log('복호화 실패, 원본 데이터 반환:', encryptedData);
        }
        return encryptedData || '';
    }
}

// AES256 암호화 함수 (서버 호환 CBC 모드)
function encryptAES256(data, secretKey = SECRET_KEY) {
    try {
        if (!secretKey) {
            console.warn('암호화 키가 제공되지 않았습니다. 원본 데이터 반환');
            return data || '';
        }

        if (!data) {
            return '';
        }

        // 이미 암호화된 데이터인지 확인
        if (isEncrypted(data)) {
            if (import.meta.env.DEV) {
                console.log('이미 암호화된 데이터입니다. 원본 반환:', data);
            }
            return data; // 이미 암호화된 데이터면 그대로 반환
        }

        // 서버와 동일한 AES/CBC/PKCS5Padding 방식으로 암호화
        const key = CryptoJS.enc.Utf8.parse(secretKey);
        
        const encrypted = CryptoJS.AES.encrypt(data, key, {
            iv: IV,
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7
        });
        
        return encrypted.toString(); // Base64 문자열 반환
    } catch (error) {
        console.error('AES256 암호화 오류:', error);
        return data || '';
    }
}

// 안전한 복호화 함수 (에러 발생 시 원본 반환)
function safeDecrypt(data, secretKey = SECRET_KEY) {
    try {
        if (!data) {
            return '';
        }
        return decryptAES256(data, secretKey);
    } catch (error) {
        if (import.meta.env.DEV) {
            console.warn('복호화 실패, 원본 데이터 반환:', data);
        }
        return data || '';
    }
}

// 환경 변수 상태 확인 함수
function getEnvStatus() {
    return {
        isDev: import.meta.env.DEV,
        isProd: import.meta.env.PROD,
        hasViteSecretKey: !!import.meta.env.VITE_SECRET_KEY,
        secretKeyLength: SECRET_KEY ? SECRET_KEY.length : 0,
        mode: import.meta.env.MODE,
        ivLength: IV.sigBytes,
        ivHex: IV.toString(CryptoJS.enc.Hex) // IV 값 확인용
    };
}

// 함수들을 export
export { decryptAES256, encryptAES256, isEncrypted, safeDecrypt, getEnvStatus, SECRET_KEY };